import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2, NotebookPen, X } from 'lucide-react';
import type { MapLayout } from '@homework/shared';
import { relativeTime } from '@homework/shared';
import { api } from '../../lib/api';
import { useMeetingNotes } from '../../lib/meetingNotes';

/**
 * Sending Mochi to a meeting room from its panel, and what it is up to while
 * it is there: where, how many people agreed, and the way to bring it back.
 */
export function MeetingVisitCard({ name, openForm, onFormClosed }: { name: string; openForm: boolean; onFormClosed: () => void }) {
  const { myVisit, justBack, recall, cancel, send } = useMeetingNotes();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  if (myVisit) {
    return (
      <div className="mx-4 mt-3 rounded-3xl bg-brand-500/[0.07] p-3 ring-1 ring-inset ring-brand-500/15">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <NotebookPen className="h-4 w-4 text-brand-700" aria-hidden />
          {myVisit.status === 'SUMMARIZING' ? `Writing up the notes from ${myVisit.roomName}…` : `At ${myVisit.roomName}, taking notes`}
        </p>
        {myVisit.status === 'ACTIVE' ? (
          <>
            <p className="mt-0.5 text-xs text-slate-600">
              Went {relativeTime(myVisit.startedAt)} · {myVisit.includedCount} {myVisit.includedCount === 1 ? 'person has' : 'people have'} agreed to be included.
              {' '}I’ll come back on my own when the room empties.
            </p>
            <div className="mt-2 flex gap-2">
              <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={() => void run('recall', () => recall(myVisit.id))} disabled={busy !== null}>
                {busy === 'recall' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                Come back with notes
              </button>
              <button
                type="button"
                className="btn-ghost px-3 py-1.5 text-xs"
                onClick={() => {
                  if (window.confirm('Come back without notes? Anything noted so far is thrown away.')) void run('cancel', () => cancel(myVisit.id));
                }}
                disabled={busy !== null}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <p className="mt-1 flex items-center gap-2 text-xs text-slate-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Turning what was said into minutes.
          </p>
        )}
        {error ? <p className="mt-2 text-xs text-rose-700" role="alert">{error}</p> : null}
      </div>
    );
  }

  return (
    <>
      {justBack ? (
        <div className="mx-4 mt-3 rounded-3xl bg-emerald-400/10 p-3 ring-1 ring-inset ring-emerald-500/20">
          <p className="text-sm font-semibold text-slate-900">I’m back from {justBack.roomName}!</p>
          {justBack.minutesFileId ? (
            <Link
              to={`/files?file=${encodeURIComponent(justBack.minutesFileId)}`}
              className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-brand-800 ring-1 ring-inset ring-brand-500/20 hover:bg-white"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              Read the minutes
            </Link>
          ) : (
            <p className="mt-0.5 text-xs text-slate-600">{justBack.error ?? 'There was nothing to note.'}</p>
          )}
          {justBack.status === 'FAILED' ? (
            <button type="button" className="btn-ghost mt-1 px-3 py-1 text-xs" onClick={() => void run('retry', () => recall(justBack.id))} disabled={busy !== null}>
              Try writing them again
            </button>
          ) : null}
          {error ? <p className="mt-2 text-xs text-rose-700" role="alert">{error}</p> : null}
        </div>
      ) : null}
      {openForm ? <SendForm name={name} onClose={onFormClosed} send={send} /> : null}
    </>
  );
}

function SendForm({ name, onClose, send }: { name: string; onClose: () => void; send: (roomId: string, focus?: string) => Promise<void> }) {
  const [rooms, setRooms] = useState<Array<{ id: string; name: string }> | null>(null);
  const [roomId, setRoomId] = useState('');
  const [focus, setFocus] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formId = useId();

  useEffect(() => {
    api
      .get<{ layout: MapLayout }>('/office/map')
      .then((response) => {
        const meetingRooms = response.layout.zones.filter((zone) => zone.type === 'MEETING').map((zone) => ({ id: zone.id, name: zone.name }));
        setRooms(meetingRooms);
        setRoomId((current) => current || meetingRooms[0]?.id || '');
      })
      .catch(() => setRooms([]));
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!roomId) return;
    setPending(true);
    setError(null);
    try {
      await send(roomId, focus.trim());
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work');
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-4 mt-3 space-y-2 rounded-3xl bg-white/70 p-3 ring-1 ring-inset ring-white">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Send {name} to a meeting</p>
        <button type="button" className="btn-ghost p-1" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <label htmlFor={`${formId}-room`} className="label">
        Room
      </label>
      <select id={`${formId}-room`} className="input" value={roomId} onChange={(event) => setRoomId(event.target.value)} disabled={!rooms}>
        {rooms === null ? <option>Loading…</option> : null}
        {rooms?.length === 0 ? <option value="">No meeting rooms on this map</option> : null}
        {rooms?.map((room) => (
          <option key={room.id} value={room.id}>
            {room.name}
          </option>
        ))}
      </select>
      <label htmlFor={`${formId}-focus`} className="label">
        Anything to listen for? (optional)
      </label>
      <input id={`${formId}-focus`} className="input" value={focus} maxLength={500} onChange={(event) => setFocus(event.target.value)} placeholder="e.g. decisions on pricing, who owns what" />
      <p className="text-[11px] leading-snug text-slate-500">
        Everyone in the room is told and asked first. Only people who agree are noted — by their own browser — and they get the minutes too.
      </p>
      {error ? <p className="text-xs text-rose-700" role="alert">{error}</p> : null}
      <button type="submit" className="btn-secondary w-full py-1.5 text-sm" disabled={pending || !roomId}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <NotebookPen className="h-4 w-4" aria-hidden />}
        Go take notes
      </button>
    </form>
  );
}
